/**
 * Local content library. These are ours: gradient cards and a short generated
 * tone. Nothing here is pulled from another product's catalog.
 */

import type { TimelineClip, VideoProject } from '@canvas/shared-types';
import { insertClip, nextClipId, TRACK } from './timelineEdit';

export interface StockItem {
  id: string;
  kind: 'gradient' | 'tone';
  title: string;
  blurb: string;
  swatch: string;
  from: string;
  to: string;
}

export const STOCK_ITEMS: StockItem[] = [
  {
    id: 'dusk',
    kind: 'gradient',
    title: 'Dusk wash',
    blurb: 'Full-frame gradient',
    swatch: 'linear-gradient(135deg,#1b1030,#6a3d9a)',
    from: '#1b1030',
    to: '#6a3d9a',
  },
  {
    id: 'tide',
    kind: 'gradient',
    title: 'Tide',
    blurb: 'Cool full-frame gradient',
    swatch: 'linear-gradient(135deg,#10243f,#1f6f8b)',
    from: '#10243f',
    to: '#1f6f8b',
  },
  {
    id: 'hearth',
    kind: 'gradient',
    title: 'Hearth',
    blurb: 'Warm full-frame gradient',
    swatch: 'linear-gradient(135deg,#3a1d12,#c4622d)',
    from: '#3a1d12',
    to: '#c4622d',
  },
  {
    id: 'soft-bed',
    kind: 'tone',
    title: 'Soft bed',
    blurb: 'Eight-second tone',
    swatch: 'linear-gradient(90deg,#243044,#3d5a73)',
    from: '#243044',
    to: '#3d5a73',
  },
];

export function stockById(id: string): StockItem | null {
  return STOCK_ITEMS.find((item) => item.id === id) ?? null;
}

/** A tiny uncompressed WAV so the library has a music bed without a binary asset. */
export function toneWav(seconds = 8, freq = 196): Blob {
  const sampleRate = 44100;
  const frames = Math.floor(sampleRate * seconds);
  const buffer = new ArrayBuffer(44 + frames * 2);
  const view = new DataView(buffer);
  const write = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };
  write(0, 'RIFF');
  view.setUint32(4, 36 + frames * 2, true);
  write(8, 'WAVE');
  write(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  write(36, 'data');
  view.setUint32(40, frames * 2, true);
  for (let i = 0; i < frames; i += 1) {
    const env = Math.min(1, i / sampleRate, (frames - i) / sampleRate);
    const sample = Math.sin((2 * Math.PI * freq * i) / sampleRate) * 0.18 * env;
    view.setInt16(44 + i * 2, Math.round(sample * 32767), true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

export function gradientClip(item: StockItem, atMs: number): TimelineClip {
  return {
    id: nextClipId('stock'),
    trackId: TRACK.visual,
    startMs: atMs,
    durationMs: 5000,
    name: item.title,
    kind: 'text',
    text: {
      text: '',
      fontFamily: 'Arial, sans-serif',
      fontSize: 32,
      fontWeight: 700,
      fill: '#ffffff',
      align: 'center',
      x: 0.5,
      y: 0.5,
      background: item.from,
      gradientTo: item.to,
      fullFrame: true,
      inPreset: 'none',
      outPreset: 'none',
    },
  };
}

export function placeGradient(video: VideoProject, itemId: string, atMs: number): VideoProject {
  const item = stockById(itemId);
  if (!item || item.kind !== 'gradient') return video;
  return insertClip(video, gradientClip(item, atMs));
}
